import { Feature, InsuranceContent, StockCode } from './ProductResponseType';
import { NonReturnable } from './NonReturnable';
import { VoucherTypes } from '../constants/promotion-voucher-constants';

export interface Product {
  identification: ProductIdentification;
  images: ProductImage[];
  brand?: string;
  name: string;
  price: ProductPrice;
  lowestPrice: ProductPrice;
  reviews: Reviews;
  skus: ProductVariant[];
  description: string;
  returnable?: NonReturnable;
  dynamicPrice?: ProductPriceAmount;
  features?: Feature[];
  personalizableMaxLength?: number;
  productAttributes?: ProductAttribute[];
  digitalDownload: boolean;
  isInsuranceAvailable: boolean;
  promotionVoucher?: PromotionVoucher | CustomPromotionVoucher;
  insuranceContent?: InsuranceContent[];
  roundel?: Roundel;
  linkedAddonServicesContent?: LinkedAddonServicesContent[];
  tradeInValue?: number;
  tradingDepartmentId?: number;
  videos?: Video[];
  insuranceRetrievalFailed?: boolean;
  promotionIdentifier?: PromotionIdentifier;
  appleRichContentKey?: string;
  weeeEligibilityInd?: boolean;
  hasDeliveryContent?: boolean;
}

export interface Video {
  orientation: string;
  url: string;
}

export interface LinkedAddonServicesContent {
  productId: string;
  optionNumber: string;
  title: string;
  description: string;
  price: LinkedAddonServicesPrice;
  isSelected?: boolean;
}

export interface LinkedAddonServicesPrice {
  now: {
    currencySymbol: string;
    currencyCode: string;
    basePrice: number;
  };
}

export interface ProductIdentification {
  ean?: string;
  itemNumber: string;
  productId: string;
}

export interface ProductImage {
  identifier: string;
}

export interface ProductPrice {
  validFrom: string;
  validUntil: string;
  amount: ProductPriceAmount;
  priceTerms?: PriceTerm[];
  longestWeeklyPriceTermWithZeroInterest?: PriceTerm;
  longestWeeklyPriceTermWithInterest?: PriceTerm;
  has208WeeklyPriceTerm?: boolean;
}

export interface PriceTerm {
  apr: number;
  termPeriod: number;
  totalSellingPrice: string;
  weeklyPaymentAmount: string;
}

export interface ProductPriceAmount {
  previous?: string;
  current: string;
  saving?: string;
  currencyCode: string;
  currencySymbol: string;
  previousTo?: string;
  previousFrom?: string;
  currentTo?: string;
  currentFrom?: string;
  currentFromUnformatted?: number;
  wasFromUnformatted?: number;
  decimal: number;
  unformattedPrice: number;
  unformattedPrevious?: number;
  unformattedSaving?: number;
  unformattedSavingTo?: number;
  savingTo?: string;
  unitPrice?: string;
  prfValue?: string;
  exPrf?: string;
}

export interface Reviews {
  total?: number;
  stars?: number;
}

export interface ProductVariant {
  images: ProductImage[];
  itemNumber: string;
  manufacturerPartNumber?: string;
  option: string;
  options: ProductVariantOptions;
  price?: ProductPrice;
  stock?: ProductVariantStock;
  swatches?: ProductSwatch;
  ean?: string;
  ffmIndicator?: boolean;
  isInsuranceAvailable?: boolean;
  insuranceOffers?: InsuranceOffer[];
  deliveryAndReturnContentKey: DeliveryAndReturnContentKey;
  linkedAddOnService?: string[];
  productTypeCode?: number;
}

export type InsuranceOffer = {
  priority: number;
  offer: {
    productId: number;
    productContentId: number;
    sellingPrice: number;
    weeklyPrices?: {
      numberOfWeeks: number;
      weeklySellingPrice: number;
    }[];
    isAutoRenewable: boolean;
    productTypeCode: number;
    productLineId: string;
    sku: string;
    suffix: string;
    outletCode: string;
    productDescription: string;
    coverLengthMonths: number;
    termDescription: string;
  };
};

export interface ProductSwatch {
  image?: string;
  colour?: string;
}

export interface ProductVariantOptions {
  [key: string]: string;
}

export interface ProductVariantPrice {
  amount: {
    previous?: string;
    current: string;
    saving?: string;
    currencyCode?: string;
    currencySymbol: string;
    decimal: number;
    unformattedPrevious?: number;
    unformattedSaving?: number;
    unformattedSavingTo?: number;
    unformattedPrice?: number;
    previousFrom?: string;
    previousTo?: string;
    currentFrom?: string;
    currentTo?: string;
    savingTo?: string;
    unitPrice?: string;
  };
  validFrom: string;
  validUntil: string;
}

export interface ProductVariantStock {
  primary: string;
  code: StockCode;
  secondary?: string;
}

export interface DeliveryAndReturnContentItem {
  pageText: string;
  pageLink: string;
  modalHeading: string;
  modalBody: string;
}

export enum DeliveryAndReturnContentKey {
  FFM = 'FFM',
  MidRange = 'MidRange',
  TwoPerson = 'TwoPerson',
  OnePerson = 'OnePerson',
}

export type DeliveryAndReturnContent = Partial<Record<DeliveryAndReturnContentKey, DeliveryAndReturnContentItem>>;

export interface ProductAttribute {
  key: string;
  value: string[];
}

export interface PromotionVoucher {
  site: string;
  voucherCode: string;
  cardDescription?: string;
  type: VoucherTypes;
  amount: number;
  startDate: string;
  endDate: string;
  offerTitle: string;
  offerTerms: string;
  offerTerms2?: string;
  offerTerms3?: string;
}

export interface CustomPromotionVoucher {
  site: string;
  startDate: string;
  endDate: string;
  cardTitle: string;
  cardDescription?: string;
  cardHyperlinkText: string;
  dialogTitle: string;
  dialogDescription: string;
}

export interface PromotionIdentifier {
  displayName: string;
  colour: string;
}

export interface Roundel {
  site: string;
  promoName: string;
  imageUrlPath: string;
}
